import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ChainThemeEnum,
  ChainCurrencyEnum,
  ChainBrandLogoDto,
  ItemPhotoTypeEnum,
  ClientChainSettingsDto,
  ChainThemeStyle,
  ChainBannerPhotoDesktopDto,
  ChainBannerPhotoAdaptiveDto,
} from '../api/restApi';

export interface InitState {
  tgData?: string;
  chainId?: string;
  brendLogo?: ChainBrandLogoDto;
  settings: Omit<ClientChainSettingsDto, 'recommendationsEnabled' | 'showDescription'>;
  viewedBanners: string[];
  catalogBanners?: {
    bannerPhotoDesktop?: ChainBannerPhotoDesktopDto;
    bannerPhotoAdaptive?: ChainBannerPhotoAdaptiveDto;
  };
}

const initialState: InitState = {
  settings: {
    showBrandLogo: false,
    cutleryEnabled: false,
    isBonusSystemEnabled: false,
    bonusSystem: null,
    eatAtPlaceOptionEnabled: false,
    noCallIsNeededOptionEnabled: false,
    leaveOrderAtDoorOptionEnabled: false,
    isOrderForAnotherPersonEnabled: false,
    handleStocks: false,
    currency: ChainCurrencyEnum.Rub,
    commentsEnabled: false,
    itemPhotoType: ItemPhotoTypeEnum.Square,
    theme: {
      accentColor: '#F97316',
      firstColor: '#FFF7ED',
      secondColor: '#FED7AA',
      thirdColor: '#FFFDFA',
      analogColor: '#10B981',
      name: ChainThemeEnum.Light,
      style: ChainThemeStyle.Base,
    },
  },
  viewedBanners: [],
};

export const initialSlice = createSlice({
  name: 'initial',
  initialState,

  reducers: {
    setChainId(state, { payload }: PayloadAction<string | undefined>) {
      state.chainId = payload;
    },
    setTgData(state, { payload }: PayloadAction<string | undefined>) {
      state.tgData = payload;
    },
    setBrendLogo(state, { payload }: PayloadAction<ChainBrandLogoDto | undefined>) {
      state.brendLogo = payload;
    },
    setCatalogBanners(state, { payload }: PayloadAction<InitState['catalogBanners']>) {
      state.catalogBanners = payload;
    },

    changeSettings(state, { payload }: PayloadAction<Omit<ClientChainSettingsDto, 'recommendationsEnabled'>>) {
      const { theme, ...rest } = payload;
      const updatedTheme = {
        accentColor: '#' + theme.accentColor,
        firstColor: '#' + theme.firstColor,
        secondColor: '#' + theme.secondColor,
        thirdColor: '#' + theme.thirdColor,
        analogColor: '#' + theme.analogColor,
        name: theme.name,
        style: theme.style,
      };
      state.settings = { ...rest, theme: updatedTheme };
    },
    addViewedBanner(state, { payload }: PayloadAction<string>) {
      if (!state.viewedBanners.includes(payload)) state.viewedBanners.push(payload);
    },
    removeViewedBanner(state, { payload }: PayloadAction<string>) {
      state.viewedBanners = state.viewedBanners.filter((b) => b !== payload);
    },
  },
});

export const {
  setTgData,
  setChainId,
  setBrendLogo,
  changeSettings,
  setCatalogBanners,
  addViewedBanner,
  removeViewedBanner,
} = initialSlice.actions;

export const initialReducer = initialSlice.reducer;

export const selectCatalogBanners = (state: { initial: InitState }) => state.initial.catalogBanners;

export const selectThemeStyle = (state: { initial: InitState }) => state.initial.settings.theme.style;

export const selectIsMinimalistic = (state: { initial: InitState }) =>
  state.initial.settings.theme.style === ChainThemeStyle.Minimalism;
